/**
 * LazyImageWithSpinner
 * 
 * Admin component that displays images with loading spinner.
 * Now uses useImageUrl hook to handle relative paths and generate signed URLs on demand.
 */

import React, { useState, useEffect } from 'react';
import { Image as ImageIcon, Loader2, ExternalLink } from 'lucide-react';
import { useImageUrl } from '@/hooks/useImageUrl';

interface LazyImageWithSpinnerProps {
  originalUrl: string;
  alt: string;
  className?: string;
  onUrlReady?: (signedUrl: string) => void;
}

const LazyImageWithSpinner: React.FC<LazyImageWithSpinnerProps> = ({
  originalUrl,
  alt,
  className = "",
  onUrlReady
}) => {
  const { url: signedUrl, isLoading } = useImageUrl(originalUrl, 'display');
  const [hasError, setHasError] = useState(false);

  // Reset error state when URL changes
  useEffect(() => {
    setHasError(false);
  }, [originalUrl]);

  // Notify parent when signed URL is ready
  useEffect(() => {
    if (signedUrl && signedUrl !== originalUrl && onUrlReady) {
      onUrlReady(signedUrl);
    }
  }, [signedUrl, originalUrl, onUrlReady]);

  const handleOpenLink = () => {
    if (signedUrl) {
      window.open(signedUrl, '_blank');
    }
  };

  if (isLoading) {
    return (
      <div className={`${className} flex items-center justify-center bg-white/5 border border-white/10 rounded-lg`}>
        <Loader2 className="h-6 w-6 text-luxury-pinkBright animate-spin" />
      </div>
    );
  }

  if (hasError || !signedUrl) {
    return (
      <div className={`${className} flex items-center justify-center bg-white/5 border border-white/10 rounded-lg`}>
        <ImageIcon className="h-6 w-6 text-white/40" />
      </div>
    );
  }

  return (
    <div className={`${className} group relative rounded-lg overflow-hidden bg-white/5 border border-white/10 hover:border-luxury-pinkBright/50 transition-all duration-200`}>
      <img
        src={signedUrl}
        alt={alt}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
        loading="lazy"
        onError={() => setHasError(true)}
      />
      
      {/* Overlay com botão de abrir link */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
        <button
          onClick={handleOpenLink}
          className="bg-luxury-pinkBright hover:bg-luxury-pinkBright/80 text-white p-2 rounded-full transition-colors"
          title="Abrir imagem"
        >
          <ExternalLink className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default LazyImageWithSpinner;
